/**
 * Helpers for preparing report HTML before injecting it into a sandboxed
 * iframe via `srcdoc`.
 *
 * The model that generates report HTML emits `<script src="https://...">`
 * tags pointing at ApexCharts, Chart.js and ECharts on cdnjs / jsdelivr.
 * In practice we've seen graphs intermittently fail to render for two
 * reasons:
 *
 *   1. **Hidden-tab layout bug** — the model often constructs charts
 *      inside `display:none` tab panels. ApexCharts / ECharts measure
 *      the container size at construction time; if it's 0×0 the chart
 *      renders blank and never recovers, even when the user clicks the
 *      tab. The model is supposed to add a tab-switch re-render hook
 *      but is inconsistent about it.
 *
 *   2. **CDN load failure** — slow networks, ad blockers, corporate
 *      proxies and the occasional cdnjs/jsdelivr blip all prevent the
 *      chart library from loading inside the sandboxed iframe. When
 *      `window.ApexCharts` is undefined the chart-init script throws
 *      synchronously and the iframe just shows empty containers.
 *
 * `prepareReportHtml` defends against both by injecting a small
 * bootstrap into the iframe's `<head>` that:
 *
 *   - Loads each chart library with a primary → 2× fallback CDN chain
 *     (cdnjs, jsdelivr, unpkg). The first to succeed wins.
 *   - Catches "X is not defined" errors from the model's chart-init
 *     scripts and re-runs them after the libraries finish loading via
 *     the fallback path.
 *   - Watches for tab visibility changes (clicks, MutationObserver on
 *     `style`/`class`/`hidden`/`aria-hidden`) and calls `resize()` on
 *     ApexCharts and ECharts instances inside newly-visible containers.
 *   - Shows a yellow banner inside the iframe if any chart library is
 *     still missing 12s after page load AND the report HTML actually
 *     references it — so users get a clear signal instead of a silent
 *     blank report.
 */

/**
 * Strip markdown code fences and normalise the model's output to a raw
 * HTML string. Mirrors the behaviour the legacy in-component
 * sanitisers had so the existing surface area is unchanged.
 */
function stripMarkdownFences(rawHtml: string): string {
  return rawHtml
    .replace(/^```html\s*\n?/i, "")
    .replace(/\n?```\s*$/i, "")
    .replace(/```/g, "")
    .trim();
}

/**
 * Replace any external Lift logo URLs with the canonical CDN asset so
 * the report keeps working when assets move / change.
 */
function rewriteLiftLogos(html: string): string {
  const liftLogoRegex =
    /<img([^>]*?)\bsrc\s*=\s*["']([^"']*(?:lift\.newengen\.com\/assets\/lift-logo\.png|lift[-_]?logo[^"']*|lift-ai-logo[^"']*|ltp-static-data[^"']*lift[^"']*))["']([^>]*?)>/gi;
  return html.replace(
    liftLogoRegex,
    '<img$1src="https://storage.googleapis.com/ltp-static-data/lift_assets/lift-ai-logo.png" style="max-width:60px;height:auto;" crossorigin="anonymous"$3>'
  );
}

/**
 * The bootstrap script. Pinned to library versions the prompt currently
 * requests — bump these in lockstep with `prompts.py`.
 *
 * NOTE: this string is itself a TS template literal, so any literal
 * backtick or ${} inside it MUST be escaped (\` and \\${) — otherwise
 * the parser will close the template early and Babel will report a
 * cryptic "Missing semicolon" error somewhere downstream. When in
 * doubt, just don't use backticks in comments inside this script.
 */
const REPORT_BOOTSTRAP_SCRIPT = `
<script>
(function () {
  if (window.__liftReportBootstrapInstalled) return;
  window.__liftReportBootstrapInstalled = true;

  // Pinned to versions referenced in the report-generation prompt so
  // the model and the bootstrap stay in sync.
  var LIB_SOURCES = {
    ApexCharts: [
      'https://cdnjs.cloudflare.com/ajax/libs/apexcharts/3.42.0/apexcharts.min.js',
      'https://cdn.jsdelivr.net/npm/apexcharts@3.42.0/dist/apexcharts.min.js',
      'https://unpkg.com/apexcharts@3.42.0/dist/apexcharts.min.js'
    ],
    Chart: [
      'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js',
      'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js',
      'https://unpkg.com/chart.js@4.4.1/dist/chart.umd.min.js'
    ],
    echarts: [
      'https://cdn.jsdelivr.net/npm/echarts@5.4.3/dist/echarts.min.js',
      'https://cdnjs.cloudflare.com/ajax/libs/echarts/5.4.3/echarts.min.js',
      'https://unpkg.com/echarts@5.4.3/dist/echarts.min.js'
    ]
  };

  var loadingState = { ApexCharts: 'idle', Chart: 'idle', echarts: 'idle' };

  function loadFromUrl(url) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = url;
      s.async = false;
      s.onload = function () { resolve(url); };
      s.onerror = function () { reject(new Error('failed: ' + url)); };
      (document.head || document.documentElement).appendChild(s);
    });
  }

  function ensureLib(globalName) {
    if (window[globalName]) {
      loadingState[globalName] = 'ready';
      return Promise.resolve();
    }
    if (loadingState[globalName] === 'loading') return Promise.resolve();
    loadingState[globalName] = 'loading';

    var urls = (LIB_SOURCES[globalName] || []).slice();
    function attempt() {
      if (window[globalName]) {
        loadingState[globalName] = 'ready';
        return Promise.resolve();
      }
      if (urls.length === 0) {
        loadingState[globalName] = 'failed';
        console.warn('[ReportBootstrap] All CDNs failed for ' + globalName);
        return Promise.reject(new Error('All CDNs failed for ' + globalName));
      }
      var next = urls.shift();
      return loadFromUrl(next).then(
        function () {
          loadingState[globalName] = 'ready';
          console.log('[ReportBootstrap] Loaded ' + globalName + ' from ' + next);
        },
        function () {
          console.warn('[ReportBootstrap] ' + globalName + ' failed from ' + next + ' — trying next');
          return attempt();
        }
      );
    }
    return attempt();
  }

  // Pre-warm all three libraries in parallel. We don't strictly need
  // ECharts unless the model used it, but pre-warming costs nothing on
  // a cached CDN and avoids the extra round-trip when it IS needed.
  var libsReady = Promise.all([
    ensureLib('ApexCharts'),
    ensureLib('Chart'),
    ensureLib('echarts')
  ]).catch(function () {});

  // -------------------------------------------------------------------
  // Capture chart-init scripts so we can re-run them if the model's
  // CDN load failed and our fallback finished loading after the inline
  // <script> already threw "X is not defined".
  // -------------------------------------------------------------------
  var chartInitFailed = false;
  window.addEventListener('error', function (event) {
    var msg = (event && event.message) || '';
    if (/(ApexCharts|Chart|echarts) is not defined/.test(msg)) {
      chartInitFailed = true;
      console.warn('[ReportBootstrap] Caught chart init failure: ' + msg);
      event.preventDefault && event.preventDefault();
    }
  }, true);

  // Run the supplied code with addEventListener temporarily intercepted
  // so any registration for events that have ALREADY FIRED
  // (DOMContentLoaded, load) is invoked synchronously. Without this,
  // re-executing a DOMContentLoaded-wrapped chart init does nothing -
  // it just registers a dead listener. See Lift-AI-MFE copy for full
  // context.
  // (NOTE: do NOT use backticks in comments here - this whole block is
  // a TS template literal and a stray backtick will close it early,
  // producing a cryptic "Expected ; but found <next token>" error.)
  function runReplayingFiredEvents(code) {
    var origDocAdd = document.addEventListener;
    var origWinAdd = window.addEventListener;
    var FIRED = { DOMContentLoaded: true, load: true };
    function patched(orig, target) {
      return function (type, listener, opts) {
        if (FIRED[type] && typeof listener === 'function') {
          try { listener.call(target, new Event(type)); } catch (e) {}
          return;
        }
        return orig.call(target, type, listener, opts);
      };
    }
    document.addEventListener = patched(origDocAdd, document);
    window.addEventListener = patched(origWinAdd, window);
    try {
      // eslint-disable-next-line no-new-func
      (new Function(code))();
    } catch (err) {
      console.warn('[ReportBootstrap] Re-run of chart init script failed:', err);
    } finally {
      document.addEventListener = origDocAdd;
      window.addEventListener = origWinAdd;
    }
  }

  function rerunInlineChartInits() {
    var scripts = Array.prototype.slice.call(document.scripts);
    scripts.forEach(function (s) {
      if (s.src) return;
      if (s.dataset && s.dataset.liftRerun) return;
      var code = s.textContent || '';
      if (!/new\\s+ApexCharts|new\\s+Chart\\s*\\(|echarts\\.init/.test(code)) return;
      try { s.dataset && (s.dataset.liftRerun = '1'); } catch (e) {}
      runReplayingFiredEvents(code);
    });
  }
  window.__liftRunReplayingFiredEvents = runReplayingFiredEvents;

  libsReady.then(function () {
    if (chartInitFailed) {
      console.log('[ReportBootstrap] Re-running chart init scripts after fallback CDN load');
      rerunInlineChartInits();
      // Give re-rendered charts a beat, then resize them in case they
      // landed inside hidden tabs.
      setTimeout(reflowAllCharts, 100);
    }
  });

  // -------------------------------------------------------------------
  // Hidden-tab → visible-tab re-render. ApexCharts / ECharts measure
  // their container at construction; if that was 0×0 the chart paints
  // blank and never recovers without a manual resize/render.
  // -------------------------------------------------------------------
  function reflowApexCharts() {
    if (!window.ApexCharts) return;
    try {
      // ApexCharts >=3 stashes instances on window.Apex._chartInstances.
      var registry = window.Apex && window.Apex._chartInstances;
      if (registry && registry.forEach) {
        registry.forEach(function (entry) {
          var inst = entry && (entry.chart || entry);
          if (!inst) return;
          try {
            if (typeof inst.windowResizeHandler === 'function') inst.windowResizeHandler();
            else if (typeof inst.update === 'function') inst.update();
          } catch (e) { /* silent */ }
        });
      }
    } catch (e) { /* silent */ }
  }

  function reflowEcharts() {
    if (!window.echarts) return;
    try {
      var nodes = document.querySelectorAll('[_echarts_instance_]');
      Array.prototype.forEach.call(nodes, function (n) {
        try {
          var inst = window.echarts.getInstanceByDom(n);
          if (inst) inst.resize();
        } catch (e) { /* silent */ }
      });
    } catch (e) { /* silent */ }
  }

  function reflowChartJs() {
    if (!window.Chart || !window.Chart.instances) return;
    try {
      Object.keys(window.Chart.instances).forEach(function (k) {
        var inst = window.Chart.instances[k];
        try { inst && inst.resize && inst.resize(); } catch (e) { /* silent */ }
      });
    } catch (e) { /* silent */ }
  }

  function reflowAllCharts() {
    reflowApexCharts();
    reflowEcharts();
    reflowChartJs();
  }

  // Coalesce reflow calls — tab switches often trigger a flurry of
  // mutations and we only need to reflow once after they settle.
  var pendingReflow = null;
  function scheduleReflow(delay) {
    if (pendingReflow) clearTimeout(pendingReflow);
    pendingReflow = setTimeout(function () {
      pendingReflow = null;
      reflowAllCharts();
    }, delay || 80);
  }

  // 1. Click-driven tab switches: most tab UIs use plain buttons/divs
  //    and toggle display / hidden on tab panels in their own click
  //    handler. Reflow shortly after every click.
  document.addEventListener('click', function () { scheduleReflow(60); }, true);

  // 2. Attribute mutations: when a tab panel goes from hidden → visible
  //    (style.display, class change, aria-hidden, hidden attribute).
  function installMutationWatcher() {
    if (!document.body) return;
    try {
      var mo = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var name = mutations[i].attributeName;
          if (name === 'style' || name === 'class' || name === 'aria-hidden' || name === 'hidden' || name === 'aria-selected') {
            scheduleReflow(80);
            return;
          }
        }
      });
      mo.observe(document.body, {
        attributes: true,
        subtree: true,
        attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'aria-selected']
      });
    } catch (e) { /* silent */ }
  }

  // 3. Window resize as a final safety net.
  window.addEventListener('resize', function () { scheduleReflow(120); });

  // -------------------------------------------------------------------
  // Failure banner. Only shown when we actually needed a library that
  // we couldn't load — prevents false positives on plain-HTML reports
  // that don't use any chart libs at all.
  // -------------------------------------------------------------------
  function showFailureBannerIfNeeded() {
    var needs = { ApexCharts: false, Chart: false, echarts: false };
    var allInline = '';
    try {
      Array.prototype.forEach.call(document.scripts, function (s) {
        if (!s.src) allInline += s.textContent || '';
      });
    } catch (e) { /* silent */ }
    if (/new\\s+ApexCharts|ApexCharts\\.exec/.test(allInline)) needs.ApexCharts = true;
    if (/new\\s+Chart\\s*\\(|Chart\\.register/.test(allInline)) needs.Chart = true;
    if (/echarts\\.init|echarts\\.use/.test(allInline)) needs.echarts = true;

    var missing = [];
    Object.keys(needs).forEach(function (lib) {
      if (needs[lib] && !window[lib]) missing.push(lib);
    });
    if (missing.length === 0) return;

    var banner = document.createElement('div');
    banner.setAttribute('data-lift-bootstrap-banner', '1');
    banner.style.cssText = [
      'position:sticky', 'top:0', 'left:0', 'right:0', 'z-index:99999',
      'background:#fff3cd', 'color:#664d03',
      'padding:10px 16px',
      'font:14px system-ui,-apple-system,sans-serif',
      'border-bottom:1px solid #ffe69c'
    ].join(';') + ';';
    banner.textContent =
      'Some chart libraries failed to load (' + missing.join(', ') +
      '). This is usually a network or ad-blocker issue — please check your connection and reload.';
    if (document.body) {
      document.body.insertBefore(banner, document.body.firstChild);
    }
  }

  // -------------------------------------------------------------------
  // Self-healing pass for empty chart containers — see Lift-AI-MFE
  // copy for the full rationale. Mirrored here so admin-app iframes
  // get the same recovery behaviour.
  // -------------------------------------------------------------------
  function findEmptyChartContainers() {
    var divs = document.querySelectorAll('div[id^="chart-"]');
    var empty = [];
    Array.prototype.forEach.call(divs, function (d) {
      if (d.querySelector('svg, canvas, [_echarts_instance_]')) return;
      empty.push(d);
    });
    return empty;
  }

  function rerunInitForChartId(chartId) {
    var scripts = Array.prototype.slice.call(document.scripts);
    var anyRerun = false;
    scripts.forEach(function (s) {
      if (s.src) return;
      var code = s.textContent || '';
      var idLiteral = "'" + chartId + "'";
      var idLiteralDq = '"' + chartId + '"';
      var hashRef = "'#" + chartId + "'";
      var hashRefDq = '"#' + chartId + '"';
      if (
        code.indexOf(idLiteral) === -1 &&
        code.indexOf(idLiteralDq) === -1 &&
        code.indexOf(hashRef) === -1 &&
        code.indexOf(hashRefDq) === -1
      ) {
        return;
      }
      try {
        // Use the replay helper so DOMContentLoaded-wrapped init code
        // (legacy reports) actually re-runs instead of registering a
        // dead listener.
        if (window.__liftRunReplayingFiredEvents) {
          window.__liftRunReplayingFiredEvents(code);
        } else {
          (new Function(code))();
        }
        anyRerun = true;
      } catch (err) {
        try {
          console.warn('[ReportBootstrap] Re-run for ' + chartId + ' threw:', err);
        } catch (e) {}
      }
    });
    return anyRerun;
  }

  function placeholderForChartId(div) {
    var ph = document.createElement('div');
    ph.setAttribute('data-lift-chart-placeholder', '1');
    ph.style.cssText = [
      'display:flex', 'align-items:center', 'justify-content:center',
      'min-height:200px', 'padding:24px',
      'border:1px dashed #d6dadf', 'border-radius:8px',
      'background:#fafbfc', 'color:#6b7280',
      'font:13px system-ui,-apple-system,sans-serif'
    ].join(';') + ';';
    ph.textContent = 'Chart unavailable — data not rendered';
    div.appendChild(ph);
  }

  function selfHealEmptyCharts() {
    var empty = findEmptyChartContainers();
    if (empty.length === 0) return;
    try {
      console.warn(
        '[ReportBootstrap] ' + empty.length +
        ' chart container(s) appear empty after initial render. Attempting re-init.',
        empty.map(function (d) { return d.id; })
      );
    } catch (e) {}

    empty.forEach(function (d) { rerunInitForChartId(d.id); });

    scheduleReflow(120);
    setTimeout(function () {
      var stillEmpty = findEmptyChartContainers();
      if (stillEmpty.length === 0) return;
      try {
        console.warn(
          '[ReportBootstrap] ' + stillEmpty.length +
          ' chart(s) still empty after re-init — installing placeholders.',
          stillEmpty.map(function (d) { return d.id; })
        );
      } catch (e) {}
      stillEmpty.forEach(placeholderForChartId);
    }, 2500);
  }

  // -------------------------------------------------------------------
  // Boot sequence
  // -------------------------------------------------------------------
  function onReady() {
    installMutationWatcher();
    // Reflow once after the page has had a beat to lay out, in case a
    // chart constructed during initial parse landed in a hidden panel.
    setTimeout(reflowAllCharts, 250);
    setTimeout(reflowAllCharts, 1000);
    // Self-heal pass — runs after libs + initial inits have had time
    // to land. Catches the most common LLM-generated chart bugs.
    setTimeout(selfHealEmptyCharts, 3500);
    // Final correctness check after we've given libs and the report
    // ample time to finish.
    setTimeout(showFailureBannerIfNeeded, 12000);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }
})();
</script>
`;

/**
 * Strip ```html fences, rewrite Lift logos, and inject the chart
 * bootstrap script into `<head>`. Returns a string suitable for
 * `iframe.srcdoc`.
 *
 * Always wraps the model output in a full HTML document if the model
 * didn't bother — the bootstrap relies on `<head>` existing so it can
 * pre-warm CDN libraries before the body's chart scripts execute.
 */
export function prepareReportHtml(rawHtml: string): string {
  if (!rawHtml) return rawHtml;

  const stripped = stripMarkdownFences(rawHtml);
  const replaced = rewriteLiftLogos(stripped);

  // Case 1: full document with a <head> we can splice into.
  if (/<head[^>]*>/i.test(replaced)) {
    return replaced.replace(/<head([^>]*)>/i, `<head$1>${REPORT_BOOTSTRAP_SCRIPT}`);
  }

  // Case 2: full <html> but no <head> — synthesise one.
  if (/<html[^>]*>/i.test(replaced)) {
    return replaced.replace(
      /<html([^>]*)>/i,
      `<html$1><head>${REPORT_BOOTSTRAP_SCRIPT}</head>`
    );
  }

  // Case 3: bare HTML fragment from the model — wrap it.
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Report Artifact</title>
  <style>body { font-family: system-ui, -apple-system, sans-serif; margin: 0; }</style>
  ${REPORT_BOOTSTRAP_SCRIPT}
</head>
<body>
${replaced}
</body>
</html>`;
}
